// External
import React, { useMemo, useState, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import classNames from "classnames";
import { useQuery } from "@apollo/client";
//Internal
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import FullScreenLoader from "UIComponents/FullScreenLoader";
// Relative
import { useAcademicRecordsModal } from "../../../../context";
import StudentListSkeleton from "../StudentListSkeleton/StudentListSkeleton";
// Design System
import {
  Modal,
  Button,
  SearchInput,
  Alert,
  List,
  Checkbox,
  Avatar,
  EmptyState,
  IconButton,
} from "@toddle-edu/ds-web";
import { ChevronLeftOutlined } from "@toddle-edu/ds-icons";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import { getStudentYearGroup } from "../../../../module/AcademicRecordsQueries";
// Styles
import classes from "./ApplyToMultipleStudentsModal.scss";

const modalBodyStyles = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  padding: 0,
};

const TEST_ID_PREFIX =
  "transcriptsV3-academicRecords-applyToMultipleStudentsModal";

const ApplyToMultipleStudentsModal = () => {
  const {
    closeModal,
    openModal,
    modalInfo: { metadata = {} } = {},
  } = useAcademicRecordsModal();
  const { currentStudent, onApply } = metadata;
  const currentStudentId = _.get(currentStudent, "id");
  const {
    id: currentStudentYearGroupId,
    grade: {
      curriculumProgram: { label: curriculumProgramsLabel } = {},
      globalGrade: { name: globalGradeName } = {},
    } = {},
  } = _.get(currentStudent, "yearGroup") || {};

  const { t } = useTranslation(["transcript", "common"]);

  const { data, networkStatus } = useQuery(getStudentYearGroup, {
    variables: {
      id: currentStudentYearGroupId,
      filters: {
        orderBy: "FULL_NAME",
        orderByDirection: "ASC",
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !currentStudentYearGroupId,
  });

  const isLoading = _.isEmpty(data) && _.includes([1, 2], networkStatus);

  const [selectedStudentIds, setSelectedStudentIds] = useState(
    new Set(currentStudentId ? [currentStudentId] : [])
  );
  const [searchText, setSearchText] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const normalizedSearchText = _.trim(searchText);

  const filteredStudents = useMemo(() => {
    const searchQuery = _.toLower(_.trim(searchText));

    return _.chain(data)
      .get("node.students.edges", [])
      .map("node")
      .filter(student => {
        if (!student) return false;
        if (student.id === currentStudentId) return false;
        if (!_.isEmpty(searchQuery)) {
          const name = _.toLower(
            getFormattedUserName({ user: student, type: "student" })
          );
          return name.includes(searchQuery);
        }

        return true;
      })
      .value();
  }, [data, searchText, currentStudentId]);

  const studentIdsSet = useMemo(
    () => new Set(filteredStudents.map(s => String(s.id))),
    [filteredStudents]
  );

  const selectedInFilteredCount = useMemo(() => {
    let count = 0;
    for (const id of selectedStudentIds) {
      if (studentIdsSet.has(String(id))) count++;
    }
    return count;
  }, [selectedStudentIds, studentIdsSet]);

  const isAllSelectedInFiltered =
    studentIdsSet.size > 0 && selectedInFilteredCount === studentIdsSet.size;

  const currentStudentListData = useMemo(() => {
    if (!currentStudent) return [];
    const name = getFormattedUserName({
      user: currentStudent,
      type: "student",
    });
    return [
      {
        id: currentStudent.id,
        value: name,
        subText: currentStudent.sourceId || "",
        prefix: (
          <Avatar
            src={currentStudent.profileImage}
            id={currentStudent.id}
            size="medium"
            name={name}
          />
        ),
        suffix: null,
        isDisabled: true,
      },
    ];
  }, [currentStudent]);

  const studentListData = useMemo(
    () =>
      filteredStudents.map(student => {
        const name = getFormattedUserName({
          user: student,
          type: "student",
        });

        return {
          id: student.id,
          value: name,
          subText: student.sourceId || "",
          prefix: (
            <Avatar
              src={student.profileImage}
              id={student.id}
              size="medium"
              name={name}
            />
          ),
          suffix: null,
        };
      }),
    [filteredStudents]
  );

  const handleSelectAll = useCallback(() => {
    if (studentIdsSet.size === 0) return;

    if (isAllSelectedInFiltered) {
      setSelectedStudentIds(
        new Set(currentStudentId ? [currentStudentId] : [])
      );
    } else {
      setSelectedStudentIds(prev => {
        const newSet = new Set(prev);
        for (const id of studentIdsSet) {
          newSet.add(id);
        }
        return newSet;
      });
    }
  }, [studentIdsSet, isAllSelectedInFiltered, currentStudentId]);

  const handleRowSelection = useCallback(
    ids => {
      const newIds = new Set(ids);
      if (currentStudentId) {
        newIds.add(currentStudentId);
      }
      setSelectedStudentIds(newIds);
    },
    [currentStudentId]
  );

  const handleApply = useCallback(() => {
    if (selectedStudentIds.size <= 1) return;
    setIsApplying(true);
    if (onApply) {
      onApply(Array.from(selectedStudentIds), ({ shouldClose = true } = {}) => {
        setIsApplying(false);
        if (shouldClose) {
          closeModal();
        }
      });
    } else {
      setIsApplying(false);
    }
  }, [selectedStudentIds, onApply, closeModal]);

  const hasFilteredStudents = studentListData.length > 0;
  const showNoSearchResults =
    !hasFilteredStudents && !_.isEmpty(normalizedSearchText);
  const selectAllLabelClassName = classNames(
    classes.selectAllLabel,
    "text-body"
  );
  let listContent;

  if (isLoading) {
    listContent = <StudentListSkeleton />;
  } else if (showNoSearchResults) {
    listContent = (
      <EmptyState
        illustration={NoSearchResultsIllustration}
        title={t("common:noResultHeading")}
      />
    );
  } else {
    listContent = (
      <>
        {hasFilteredStudents && (
          <div className={classes.selectAllRow}>
            <div className={selectAllLabelClassName}>
              {t("common:select_all")}
            </div>
            <Checkbox
              testId={`${TEST_ID_PREFIX}-apply-multiple-students-modal-select-all`}
              label={t("common:select_all")}
              isChecked={isAllSelectedInFiltered}
              isIndeterminate={!isAllSelectedInFiltered}
              onChange={handleSelectAll}
            />
          </div>
        )}
        {currentStudent && (
          <div className={classes.currentStudentList}>
            <List
              data={currentStudentListData}
              showSelection={true}
              selectionType="checkbox"
              selectItemPosition="right"
              rowsSelected={[currentStudent.id]}
              onRowSelection={() => {}}
              shouldStopCheckboxPropagation={true}
              disabledRows={[currentStudent.id]}
              testId={`${TEST_ID_PREFIX}-apply-multiple-students-modal-current-student-list`}
            />
          </div>
        )}
        <List
          data={studentListData}
          showSelection={true}
          selectionType="checkbox"
          selectItemPosition="right"
          rowsSelected={Array.from(selectedStudentIds)}
          onRowSelection={handleRowSelection}
          shouldStopCheckboxPropagation={true}
          testId={`${TEST_ID_PREFIX}-apply-multiple-students-modal-list`}
        />
      </>
    );
  }

  return (
    <Modal width={532} height={708} onClose={closeModal}>
      <Modal.Header
        onClose={closeModal}
        testId={`${TEST_ID_PREFIX}-apply-multiple-students-modal-header`}
      >
        <div className={classes.headerTitleRow}>
          <IconButton
            icon={<ChevronLeftOutlined />}
            onClick={() =>
              openModal("DISPLAY_SETTINGS", { fromMultipleStudents: true })
            }
            size="small"
            type="outlined"
            testId={`${TEST_ID_PREFIX}-apply-multiple-students-modal-back-button`}
          />
          <h6 style={{ display: "inline" }}>
            {t("transcript:apply_to_multiple_students_title")}
          </h6>
        </div>
      </Modal.Header>
      <Modal.Body isScrollable={false} style={modalBodyStyles}>
        <div className={classes.container}>
          <div className={classes.alertContainer}>
            <Alert
              type="info"
              message={t(
                "transcript:apply_to_multiple_students_alert_message",
                {
                  globalGradeName,
                  curriculumProgramsLabel,
                }
              )}
            />
          </div>

          <div className={classes.searchRow}>
            <SearchInput
              testId={`${TEST_ID_PREFIX}-apply-multiple-students-modal-search`}
              placeholder={t("common:search_for_with_label", {
                label: t("common:student_plural"),
              })}
              size="medium"
              focusSearchBySlash={true}
              value={searchText}
              onChange={setSearchText}
            />
          </div>
          <div className={classes.listContainer}>{listContent}</div>
          {isApplying && <FullScreenLoader />}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.footer}>
          <div className={classes.footerLeft}>
            {!showNoSearchResults && (
              <div className="text-label">
                {t("transcript:count_student_selected", {
                  count: selectedStudentIds.size,
                })}
              </div>
            )}
          </div>
          <div className={classes.footerRight}>
            <Button
              variant="neutral"
              type="outlined"
              size="medium"
              testId={`${TEST_ID_PREFIX}-apply-multiple-students-modal-back`}
              onClick={() =>
                openModal("DISPLAY_SETTINGS", { fromMultipleStudents: true })
              }
            >
              {t("common:back")}
            </Button>
            <Button
              variant="progressive"
              size="medium"
              disabled={selectedStudentIds.size <= 1}
              testId={`${TEST_ID_PREFIX}-apply-multiple-students-modal-apply`}
              onClick={handleApply}
            >
              {t("common:apply")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(ApplyToMultipleStudentsModal);
